import { isNonNullish, pickBy } from 'remeda';
import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';

import { type Collapsing, type Filters } from '@@api/services/auth/schemas';
import { type ContentLocale } from '@@api/utils/schemas/schemas';
import { type SearchNamespace } from '@@containers/Search/types';
import { type ContentLanguage, LANG_CODES, type LangCode } from '@@lib/i18n/constants';

import { isMetadataContentLocaleRoute } from './utils';

const SETTINGS_STORE = 'settingsStore';

export type SettingsState = {
    contentLocale: ContentLanguage;
    betaFeaturesEnabled: boolean;
    collapsing: Collapsing | null;
    filters: Filters | null;
    uiLocale: LangCode;
    /** The user whose preferences the store keeps, so that their pages can start before the server answers */
    preferencesUserId: string | null;
    metadataContentLocale: ContentLanguage;
    pathname: string | undefined;
};

type SettingsActions = {
    hydrateSettings: (preferences: Partial<SettingsState>) => void;
    setMetadataContentLocale: (locale: ContentLocale) => void;
    setPathname: (pathname: string | undefined) => void;
    clearSettings: () => void;
};

export const initialState: SettingsState = {
    contentLocale: LANG_CODES.GERMAN,
    betaFeaturesEnabled: false,
    collapsing: null,
    filters: null,
    uiLocale: LANG_CODES.ENGLISH,
    preferencesUserId: null,
    metadataContentLocale: LANG_CODES.GERMAN,
    pathname: undefined,
};

export const useSettingsStore = create<SettingsState & SettingsActions>()(
    devtools(
        subscribeWithSelector(
            persist(
                (set) => ({
                    ...initialState,

                    hydrateSettings: (preferences) =>
                        set(
                            (state) => ({
                                ...state,
                                ...preferences,
                            }),
                            true,
                            'hydrateSettings',
                        ),

                    setMetadataContentLocale: (locale) =>
                        set({ metadataContentLocale: locale }, false, 'setMetadataContentLocale'),

                    setPathname: (pathname) => set({ pathname }, false, 'setPathname'),

                    clearSettings: () => set({ ...initialState }, false, 'clearSettings'),
                }),
                {
                    name: SETTINGS_STORE,
                    version: 1,
                    partialize: (state) => ({
                        contentLocale: state.contentLocale,
                        uiLocale: state.uiLocale,
                        betaFeaturesEnabled: state.betaFeaturesEnabled,
                        collapsing: state.collapsing,
                        filters: state.filters,
                        preferencesUserId: state.preferencesUserId,
                    }),
                },
            ),
        ),
        { name: SETTINGS_STORE, enabled: true },
    ),
);

export const selectContentLocale = (state: SettingsState) => {
    const isMetadataRoute = state.pathname && isMetadataContentLocaleRoute(state.pathname);
    if (isMetadataRoute && state.metadataContentLocale) {
        return state.metadataContentLocale;
    }

    if (state.contentLocale) {
        return state.contentLocale;
    }

    return LANG_CODES.GERMAN;
};

export const selectFilters = (namespace: SearchNamespace) => (state: SettingsState) => {
    const contentLocale = selectContentLocale(state);

    return state.filters?.[contentLocale]?.[namespace];
};

export const useContentLocale = () => useSettingsStore(selectContentLocale);

/** The content language, for code that runs outside React, such as route loaders */
export const getContentLocale = () => selectContentLocale(useSettingsStore.getState());

/** The interface language, for code that runs outside React, such as route loaders */
export const getUiLocale = () => useSettingsStore.getState().uiLocale;

/** The filters the user saved for a content language, without the ones they cleared */
export const getSavedFilters = (contentLocale: ContentLanguage, namespace: SearchNamespace) =>
    pickBy(
        useSettingsStore.getState().filters?.[contentLocale]?.[namespace] ?? {},
        // A cleared filter is saved as `null`, and an emptied one as `[]` or `''`
        (value) => isNonNullish(value) && value.length > 0,
    );

export const useNamespaceFilters = (namespace: SearchNamespace) =>
    useSettingsStore(selectFilters(namespace));
